import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { WorkoutCreatorMetadata } from '@/components/programming/WorkoutCreatorMetadata';
import {
  WorkoutForm,
  type WorkoutFormData,
  normalizeWorkoutFormData,
} from '@/components/programming/WorkoutForm';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAdminApi } from '@/hooks/useAdminApi';
import { useSubmissionLock } from '@/hooks/useSubmissionLock';
import { type WorkoutAttribution, getProgrammingScoringLabel } from '@ergshoot/shared';
import {
  AlertCircle,
  Clock,
  Dumbbell,
  FileText,
  Loader2,
  Pencil,
  Plus,
  Search,
  Trash2,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

interface Workout {
  id: string;
  name: string;
  description: string | null;
  movements: string[];
  scoring_type: string;
  scoring_direction: string;
  equipment: string[];
  scheduled_date: string;
  track: { id: string; name: string | null } | null;
  created_at: string;
  created_by_username?: string | null;
  attribution?: WorkoutAttribution | null;
}

interface Track {
  id: string;
  name: string;
  description: string | null;
  is_active: boolean;
  workout_count: number;
  created_at: string;
}

interface CreateContentWorkoutPayload {
  name: string;
  description?: string;
  track_id?: string;
  scoring_type: string;
  scoring_direction: string;
  movements: string[];
  equipment: string[];
  scheduled_date: string;
}

interface UpdateContentWorkoutPayload {
  name: string;
  description?: string;
  track_id?: string;
  scoring_type: string;
  scoring_direction: string;
  movements: string[];
  equipment: string[];
}

export default function AdminContent() {
  const adminApi = useAdminApi();

  // Data state
  const [workouts, setWorkouts] = useState<Workout[]>([]);
  const [tracks, setTracks] = useState<Track[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [trackFilter, setTrackFilter] = useState('');

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingWorkout, setEditingWorkout] = useState<Workout | null>(null);
  const [formInitialValues, setFormInitialValues] = useState<Partial<WorkoutFormData>>();
  const [createScheduledDate, setCreateScheduledDate] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const createSubmission = useSubmissionLock<CreateContentWorkoutPayload>();
  const updateSubmission = useSubmissionLock<UpdateContentWorkoutPayload>();
  const isSubmissionLocked = createSubmission.isLocked || updateSubmission.isLocked;

  // Fetch data on mount
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const [workoutsRes, tracksRes] = await Promise.all([
        adminApi.getProgramming({ limit: 200 }),
        adminApi.getTracks(),
      ]);

      if (tracksRes.success && tracksRes.data) {
        setTracks(tracksRes.data.tracks);
      }

      if (workoutsRes.success && workoutsRes.data) {
        setWorkouts(workoutsRes.data.workouts);
      }
    } catch (err) {
      console.error('Failed to fetch content:', err);
      setError('Failed to load workout content');
      toast.error('Failed to load workout content');
    } finally {
      setIsLoading(false);
    }
  }, [adminApi]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const closeForm = () => {
    createSubmission.reset();
    updateSubmission.reset();
    setShowModal(false);
    setEditingWorkout(null);
    setFormInitialValues(undefined);
  };

  // Open modal for new workout
  const handleAddWorkout = () => {
    createSubmission.reset();
    updateSubmission.reset();
    setEditingWorkout(null);
    setFormInitialValues(undefined);
    setCreateScheduledDate(new Date().toISOString().split('T')[0] ?? '');
    setShowModal(true);
  };

  // Open modal for editing
  const handleEditWorkout = (workout: Workout) => {
    createSubmission.reset();
    updateSubmission.reset();
    setEditingWorkout(workout);
    setFormInitialValues({
      name: workout.name,
      description: workout.description || '',
      track_id: workout.track?.id || '',
      scoring_type: workout.scoring_type,
      scoring_direction: workout.scoring_direction,
      movements: workout.movements?.join('\n') || '',
      equipment: workout.equipment?.join(', ') || '',
    });
    setShowModal(true);
  };

  // Save workout
  const handleSave = async (formData: WorkoutFormData) => {
    if (!formData.name.trim()) {
      toast.error('Please enter a workout name');
      return;
    }

    const normalized = normalizeWorkoutFormData(formData);

    if (editingWorkout) {
      const payload: UpdateContentWorkoutPayload = {
        name: normalized.name,
        description: normalized.description || undefined,
        track_id: normalized.track_id,
        scoring_type: normalized.scoring_type,
        scoring_direction: normalized.scoring_direction,
        movements: normalized.movements,
        equipment: normalized.equipment,
      };
      const attempt = updateSubmission.begin(payload);
      if (!attempt) return;

      setIsSaving(true);
      let succeeded = false;
      try {
        const response = await adminApi.updateWorkout(
          editingWorkout.id,
          attempt.payload,
          attempt.idempotencyKey
        );

        if (response.success) {
          succeeded = true;
          toast.success('Workout updated successfully');
          closeForm();
          fetchData();
        } else {
          toast.error(response.error || 'Failed to update workout');
        }
      } catch (err) {
        console.error('Save error:', err);
        toast.error('Failed to save workout');
      } finally {
        if (succeeded) updateSubmission.reset();
        else updateSubmission.release();
        setIsSaving(false);
      }
      return;
    }

    const payload: CreateContentWorkoutPayload = {
      name: normalized.name,
      description: normalized.description || undefined,
      track_id: normalized.track_id,
      scoring_type: normalized.scoring_type,
      scoring_direction: normalized.scoring_direction,
      movements: normalized.movements,
      equipment: normalized.equipment,
      scheduled_date: createScheduledDate,
    };
    const attempt = createSubmission.begin(payload);
    if (!attempt) return;

    setIsSaving(true);
    let succeeded = false;
    try {
      const response = await adminApi.createWorkout(attempt.payload, attempt.idempotencyKey);

      if (response.success) {
        succeeded = true;
        toast.success('Workout created successfully');
        closeForm();
        fetchData();
      } else {
        toast.error(response.error || 'Failed to create workout');
      }
    } catch (err) {
      console.error('Save error:', err);
      toast.error('Failed to save workout');
    } finally {
      if (succeeded) createSubmission.reset();
      else createSubmission.release();
      setIsSaving(false);
    }
  };

  // Delete workout
  const handleDeleteWorkout = async (workout: Workout) => {
    if (!confirm(`Are you sure you want to delete "${workout.name}"?`)) {
      return;
    }

    try {
      const response = await adminApi.deleteWorkout(workout.id);
      if (response.success) {
        toast.success('Workout deleted successfully');
        fetchData();
      } else {
        toast.error(response.error || 'Failed to delete workout');
      }
    } catch (err) {
      console.error('Delete error:', err);
      toast.error('Failed to delete workout');
    }
  };

  // Filter workouts
  const filteredWorkouts = workouts.filter((w) => {
    const matchesSearch =
      !searchQuery ||
      w.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      w.description?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesTrack = !trackFilter || w.track?.id === trackFilter;

    return matchesSearch && matchesTrack;
  });

  // Track options for select
  const trackOptions = [
    { value: '', label: 'All Tracks' },
    ...tracks.map((t) => ({ value: t.id, label: t.name })),
  ];

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gradient-gold">Workout Content</h1>
          <p className="mt-1 text-muted-foreground">
            Manage workout definitions, movements, and equipment requirements.
          </p>
        </div>
        <Button className="gap-2" onClick={handleAddWorkout}>
          <Plus className="h-4 w-4" />
          Create Workout
        </Button>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-3 mb-8">
        <Card className="animate-fade-in">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="rounded-lg bg-primary/10 p-3">
                <FileText className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Workouts</p>
                <p className="text-2xl font-bold">{isLoading ? '...' : workouts.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="animate-fade-in" style={{ animationDelay: '50ms' }}>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="rounded-lg bg-blue-500/10 p-3">
                <Dumbbell className="h-6 w-6 text-blue-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Active Tracks</p>
                <p className="text-2xl font-bold">
                  {isLoading ? '...' : tracks.filter((t) => t.is_active).length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="animate-fade-in" style={{ animationDelay: '100ms' }}>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="rounded-lg bg-green-500/10 p-3">
                <Clock className="h-6 w-6 text-green-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">For Time Workouts</p>
                <p className="text-2xl font-bold">
                  {isLoading ? '...' : workouts.filter((w) => w.scoring_type === 'time').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="py-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search workouts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="w-full sm:w-48">
              <Select
                options={trackOptions}
                value={trackFilter}
                onChange={(e) => setTrackFilter(e.target.value)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error State */}
      {error && (
        <Card className="mb-6 border-destructive">
          <CardContent className="py-4">
            <div className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              <span>{error}</span>
              <Button variant="outline" size="sm" className="ml-auto" onClick={fetchData}>
                Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Workouts Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            All Workouts
            <Badge variant="secondary" className="ml-2">
              {filteredWorkouts.length} workouts
            </Badge>
          </CardTitle>
          <CardDescription>
            Workout definitions with movements, scoring, and equipment
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : filteredWorkouts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="rounded-full bg-muted p-4 mb-4">
                <FileText className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold mb-1">No workouts found</h3>
              <p className="text-muted-foreground max-w-md">
                {searchQuery || trackFilter
                  ? 'Try adjusting your search or filter criteria.'
                  : 'Create your first workout to get started.'}
              </p>
              {!searchQuery && !trackFilter && (
                <Button className="mt-4" onClick={handleAddWorkout}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Workout
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Track</TableHead>
                  <TableHead>Scoring</TableHead>
                  <TableHead>Movements</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredWorkouts.map((workout, index) => (
                  <TableRow
                    key={workout.id}
                    className="animate-fade-in"
                    style={{ animationDelay: `${index * 30}ms` }}
                  >
                    <TableCell>
                      <div>
                        <p className="font-medium">{workout.name}</p>
                        {workout.description && (
                          <p className="text-sm text-muted-foreground line-clamp-1">
                            {workout.description}
                          </p>
                        )}
                        <WorkoutCreatorMetadata
                          attribution={workout.attribution}
                          created_at={workout.created_at}
                          created_by_username={workout.created_by_username}
                          className="mt-1"
                        />
                      </div>
                    </TableCell>
                    <TableCell>
                      {workout.track ? (
                        <Badge variant="outline">{workout.track.name}</Badge>
                      ) : (
                        <span className="text-muted-foreground">Unassigned</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <Badge variant="secondary">
                          {getProgrammingScoringLabel(workout.scoring_type)}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {workout.scoring_direction === 'ascending'
                            ? 'Lower is better'
                            : 'Higher is better'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {workout.movements?.length > 0 ? (
                        <span className="text-sm">
                          {workout.movements.slice(0, 2).join(', ')}
                          {workout.movements.length > 2 && ` +${workout.movements.length - 2}`}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">None defined</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditWorkout(workout)}
                        >
                          <Pencil className="h-3 w-3 mr-1" />
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                          onClick={() => handleDeleteWorkout(workout)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-lg mx-4 animate-fade-in max-h-[90vh] overflow-y-auto">
            <WorkoutForm
              variant="content"
              title={editingWorkout ? 'Edit Workout' : 'Create Workout'}
              subtitle={
                editingWorkout ? 'Update workout content' : 'Create a new workout definition'
              }
              tracks={tracks.map((track) => ({ id: track.id, name: track.name }))}
              submitLabel={editingWorkout ? 'Save Changes' : 'Create Workout'}
              headerIcon={<FileText className="h-5 w-5 text-primary" />}
              initialValues={formInitialValues}
              isSubmitting={isSubmissionLocked || isSaving}
              fieldIds={{
                name: 'workout-name',
                description: 'workout-description',
                movements: 'workout-movements',
                equipment: 'workout-equipment',
              }}
              onSubmit={handleSave}
              onCancel={closeForm}
            />
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
